<template>
  <div
    class="ns-product-detail"
    :class="{ 'is-sale': isSale }"
    itemscope
    itemtype="http://schema.org/Product"
  >
    <!-- --------------- -->
    <!-- ADD AS FAVORITE -->
    <!-- --------------- -->
    <div
      v-if="!isBaseSku && !hideFavorites"
      class="ns-product-detail__favorite"
    >
      <div
        class="ns-product-detail__favorite-icon"
        :class="{ 'is-favorite': isFavorite }"
        data-testid="favoriteIcon"
        :data-testid-value="isFavorite"
        @click="handleFavoriteClick"
        @mouseover="handleShowFavoritesText(true)"
        @mouseleave="handleShowFavoritesText(false)"
      >
        <NsIcon v-if="isFavorite" icon-name="icon-favorite-heart-hollow" />
        <NsIcon v-else icon-name="icon-favorite-heart" />
      </div>
      <span
        v-show="showFavoritesText && $mq !== 'mobile' && $mq !== 'phablet'"
        class="ns-product-detail__favorite-text"
      >
        {{ translations.addToFavorites }}
      </span>
    </div>

    <div class="ns-product-detail__main-content">
      <!-- --------- -->
      <!-- LEFT SIDE -->
      <!-- --------- -->

      <div class="ns-product-detail__body--left">
        <div class="ns-product-detail__images">
          <!-- IMAGE (Show on tablet and up || if only one img) -->
          <NsImage
            v-if="
              image.contentType === 'img' &&
                (($mq !== 'mobile' && $mq !== 'phablet') ||
                  imageSet.length <= 1)
            "
            class="ns-product-detail__active-image"
            :class="{ transitioning: slideTransitioning }"
            :src="image.src"
            :alt="image.alt"
            :fallback-src="imageFallbackSrc"
            maintain-ratio="1x1"
            image-fit="contain"
            use-src-set
            itemprop="image"
          >
            <div
              v-if="isSale && salesLabel"
              class="ns-product-detail__sales-label"
            >
              {{ salesLabel }}
            </div>
          </NsImage>

          <!-- VIDEO (Show on tablet and up || if only one img) -->
          <video
            v-if="
              image.contentType === 'video' &&
                (($mq !== 'mobile' && $mq !== 'phablet') ||
                  imageSet.length <= 1)
            "
            ref="activeVideo"
            :key="image.src"
            class="ns-product-detail__active-image ns-product-detail__active-video"
            controls
          >
            <source :src="image.src" />
          </video>

          <!-- IMAGE CAROUSEL (takes place of image on mobile and phablet)-->
          <template v-if="imageSet.length > 1">
            <div class="ns-product-detail-carousel__slide-number">
              {{ slideIndex + 1 }} / {{ imageSet.length }}
            </div>
            <NsProductDetailCarousel
              :image-set="imageSet"
              :slide-index="slideIndex"
              @slide-select="handleSlideSelect"
            />
          </template>
        </div>
      </div>

      <!-- ---------- -->
      <!-- RIGHT SIDE -->
      <!-- ---------- -->

      <div class="ns-product-detail__body--right">
        <!-- TITLE -->
        <h1 class="ns-product-detail__title" itemprop="name" v-html="title" />

        <!-- SIZE -->
        <div
          v-if="sizeWeight"
          class="ns-product-detail__size-weight"
          itemprop="weight"
        >
          <span data-testid="sizeLabel">{{ translations.size }}</span>
          <span
            class="ns-product-detail__weight"
            data-testid="sizeWeight"
            v-html="sizeWeight"
          />
        </div>

        <!-- PRICES -->
        <NsProductPrice
          v-if="showPrices"
          class="ns-product-detail__price"
          :adr-only="showAdrOnly"
          :adr-price="adrPrice"
          :adr-price-label="translations.adrPriceLabel"
          :adr-price-type="adrPriceType"
          :is-base-sku="isBaseSku"
          :min-price="minPrice"
          :max-price="maxPrice"
          :original-price="originalPrice"
          :original-price-label="originalPriceLabel"
          :original-price-type="originalPriceType"
          :price="price"
          :price-label="priceLabel"
          :price-type="priceType"
          :show-labels="showPriceLabels"
          :show-original-crossed-out="showOriginalCrossedOut"
          :show-adr-price="showAdrPrice"
          :hide-adr-override="hideAdr"
        />

        <!-- SKU & POINTS -->
        <NsProductSkuPoints
          v-if="showSkuPoints"
          class="ns-product-detail__sku-points"
          :forcesv="forcesv"
          :hide-sku="hideSku"
          :is-distributor="isDistributor"
          :points="points"
          :sku="sku"
          :translations="translations"
          is-product-detail
        />

        <!-- LOYALTY INFORMATION -->
        <NsLoyaltyInformation
          v-if="isLoyaltyEnabled"
          class="ns-product-detail__loyalty-information"
          :translations="translations"
          :is-logged-in="isLoggedIn"
          :is-opted-in="userLoyalty.optInStatus"
          :loyalty-status-and-links="loyaltyStatusAndLinks"
          :user-loyalty="userLoyalty"
          :market-attributes="marketAttributes"
          product-detail
          :feature-flags="featureFlags"
        />

        <!-- EXCLUSIVE OFFER -->
        <div
          v-if="isExclusive && checkedQualifications"
          class="ns-product-detail__exclusive"
        >
          <!-- USER QUALIFIED -->
          <div v-if="userIsQualified" class="exclusive_title">
            <NsIcon
              class="ns-product-detail__exclusive-icon"
              icon-name="icon-exclusive-offer"
            />
            <span>{{ translations.exclusiveOfferTitle }}</span>
          </div>
          <!-- USER NOT YET QUALIFIED -->
          <NsMessageAction
            v-if="!userIsQualified"
            :message="exclusiveOfferMessage"
            :action-label="translations.signIn"
            action-type="login"
          >
            <template v-slot:above-message>
              <div class="exclusive_title">
                <NsIcon
                  class="ns-product-detail__exclusive-icon"
                  icon-name="icon-exclusive-offer"
                />
                <span class="ns-product-detail__exclusive-text">
                  {{ translations.exclusiveOfferTitle }}
                </span>
              </div>
            </template>
          </NsMessageAction>
        </div>

        <!-- VARIANT AND QTY SELECTORS -->
        <div
          v-if="showVariantSelector || (!isBaseSku && !desktopAndUp)"
          class="ns-product-detail__selectors"
        >
          <NsProductVariantSelector
            v-if="showVariantSelector"
            class="ns-product-detail__variant-selector"
            :label="translations.variantDropdownLabel"
            :placeholder="translations.variantDropdownPlaceholder"
            :selected-sku="isBaseSku ? '' : sku"
            :type="translations.variantType"
            :disabled="false"
            :tall="!desktopAndUp"
            :variant-skus="variantSkus"
            :variants="variants"
            @select="handleSelectVariant"
          />
          <NsProductQuantitySelector
            v-if="!isBaseSku && !desktopAndUp"
            class="ns-product-detail__quantity-selector"
            :class="{
              'no-variant': !showVariantSelector || hideVariantSelector
            }"
            :selected="quantity"
            :disabled="disableQuantitySelector"
            :max-available="maxQuantity"
            :inline-label="
              phabletAndDown && (!showVariantSelector || hideVariantSelector)
            "
            :translations="translations"
            wide
            @select="handleQuantitySelect"
          />
        </div>

        <!-- QTY SELECTOR & ACTION BUTTONS -->
        <template v-if="!isBaseSku">
          <div class="ns-product-detail__actions">
            <NsProductQuantitySelector
              v-if="!isBaseSku && desktopAndUp"
              :selected="quantity"
              :disabled="disableQuantitySelector"
              :max-available="maxQuantity"
              :translations="translations"
              @select="handleQuantitySelect"
            />

            <!-- ACTION BUTTONS -->
            <button
              class="ns-product-detail__add-to-cart fluid primary-solid"
              :disabled="disableAddToCart"
              data-testid="addToCart"
              @click="handleAddToCart"
            >
              <NsIcon icon-name="icon-smallcart-empty" />
              {{ translations.addToCart }}
            </button>
            <button
              v-if="!hideAdr"
              class="ns-product-detail__add-to-adr fluid secondary-outline"
              :disabled="disableAddToAdr"
              data-testid="addToAdr"
              @click="handleAddToAdr"
            >
              <NsIcon icon-name="icon-refresh" />
              {{ translations.addToAdr }}
            </button>
          </div>

          <!-- MESSAGE -->
          <div
            v-if="statusMessage"
            class="ns-product-detail__message"
            data-testid="status-message"
            :data-testid-value="statusMessageKey"
          >
            {{ statusMessage }}
          </div>

          <!-- PAYPAL PAY LATER -->
          <div
            v-if="showPayLater"
            class="ns-product-detail__pay-later"
            data-testid="payLater"
          >
            <NsPayPalPayLater :amount="payLaterAmount" placement="product" />
          </div>

          <!-- LOYALTY GIFT -->
          <!-- <button
              v-if="!hideAdr"
              class="ns-product-detail__add-to-loyalty fluid secondary-outline"
              :disabled="disableAddToAdr"
              @click="addLoyaltyGift"
            >
              <NsIcon icon-name="icon-refresh" />
              {{ translations.chooseGift }}
            </button> -->
        </template>

        <!-- DESCRIPTION -->
        <div
          v-if="description"
          class="ns-product-detail__description"
          itemprop="description"
          v-html="description"
        />

        <!-- SALE EVENT TEXT -->
        <div
          v-if="isSale"
          class="ns-product-detail__sales-event-text"
          itemprop="salesEventText"
          v-html="salesEventText"
        />

        <!-- NETTO WEIGHT -->
        <div
          v-if="nettoWeight"
          class="ns-product-detail__netto-weight"
          data-testid="nettoLabel"
        >
          {{ translations.nettoWeight }}
          <span data-testid="nettoWeight" v-html="nettoWeight" />
        </div>

        <!-- SHIPPING & REFUND -->
        <div v-if="!isBaseSku" class="ns-product-detail__shipping-refunds">
          <slot name="shipping" />
        </div>
        <div v-if="!isBaseSku" class="ns-product-detail__shipping-refunds">
          <slot name="refunds" />
        </div>
      </div>
    </div>

    <!-- ----------- -->
    <!-- Detail Info -->
    <!-- ----------- -->
    <div v-if="!hideInfo" class="ns-product-detail__info">
      <div class="ns-product-detail__info-column">
        <NsProductDetailInfo
          v-for="item in columnOne"
          :key="item.id"
          :title="item.title"
          :content="item.content"
          :content-type="item.type"
        />
      </div>
      <div v-if="columnTwo.length" class="ns-product-detail__info-column">
        <NsProductDetailInfo
          v-for="item in columnTwo"
          :key="item.id"
          :title="item.title"
          :content="item.content"
          :content-type="item.type"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { NsIcon } from "@nuskin/design-components";
import NsImage from "./NsImage.vue";
import NsProductDetailCarousel from "./NsProductDetailCarousel.vue";
import NsProductVariantSelector from "./NsProductVariantSelector.vue";
import NsMessageAction from "./NsMessageAction.vue";
import NsProductPrice from "./NsProductPrice.vue";
import NsProductSkuPoints from "./NsProductSkuPoints.vue";
import NsLoyaltyInformation from "./NsLoyaltyInformation.vue";
import NsProductQuantitySelector from "./NsProductQuantitySelector.vue";
import NsProductDetailInfo from "./NsProductDetailInfo.vue";
import NsPayPalPayLater from "./NsPayPalPayLater.vue";
import "@nuskin/ns-core-styles/src/dist/main.css";

/**
 * NsProductDetailBase
 *
 * How to use: You shouldn't use this component. Use NsProductDetail instead. It
 * includes the Product mixin. This component is simply all the html and styles.
 *
 * @event variant-select  When a variant selection occurs, this event emits a
 * function with params: (value, index)
 * @event quantity-select  When a quantity selection occurs, this event emits a
 * function with params: (value, index)
 * @event add-to-cart  When clicking on "Add to Cart", this event emits a
 * function with params: ()
 * @event add-to-adr  When clicking on "Add to Adr", this event emits a function
 * with params: ()
 * @event favorite-click  When clicking on "Favorites" icon, this event emits
 * a function with params: ()
 * @event image-select  When clicking on the image carousel, this event emits
 * a function with params: (index)
 */

export default {
  name: "NsProductDetailBase",
  components: {
    NsIcon,
    NsImage,
    NsProductDetailCarousel,
    NsProductVariantSelector,
    NsProductQuantitySelector,
    NsMessageAction,
    NsProductPrice,
    NsProductSkuPoints,
    NsLoyaltyInformation,
    NsProductDetailInfo,
    NsPayPalPayLater
  },
  props: {
    /**
     * ADR price value
     */
    adrPrice: {
      type: String,
      default: ""
    },
    adrPriceType: {
      type: String,
      default: ""
    },
    checkedQualifications: Boolean,
    /**
     * Product description
     */
    description: {
      type: String,
      default: ""
    },
    /**
     * Disables Add to ADR button
     */
    disableAddToAdr: Boolean,
    /**
     * Disables Add to Cart button
     */
    disableAddToCart: Boolean,
    /**
     * Disables the quantity selector
     */
    disableQuantitySelector: Boolean,
    /**
     * Enables the PayPal text, used to determine if we should show the Pay Later component
     */
    enablePayPalText: Boolean,
    /**
     * Message that will be shown on the exclusive offer ns-message-action component
     */
    exclusiveOfferMessage: {
      type: String,
      default: ""
    },
    forcesv: Boolean,
    featureFlags: {
      type: Object,
      default() {
        return {};
      }
    },
    /**
     * Override hiding all components associated with ADR
     */
    hideAdr: Boolean,
    hideExclusiveIcon: Boolean,
    hideExclusiveTextOnMobile: Boolean,
    hideFavorites: Boolean,
    hideInfo: Boolean,
    // TODO: class to prop
    hideSku: Boolean,
    imageFallbackSrc: {
      type: String,
      default: ""
    },
    image: {
      type: Object,
      default() {
        return {};
      }
    },
    /**
     * Array of all images for a product
     */
    imageSet: {
      type: Array,
      default() {
        return [];
      }
    },
    isBaseSku: Boolean,
    isDistributor: Boolean,
    isExclusive: Boolean,
    isFavorite: Boolean,
    isLoggedIn: Boolean,
    isLoyaltyEnabled: Boolean,
    isSale: Boolean,
    loyaltyStatusAndLinks: {
      type: Object,
      default() {
        return {};
      }
    },
    maxQuantity: {
      type: Number,
      default: null
    },
    marketAttributes: {
      type: Object,
      default() {
        return {};
      }
    },
    maxPrice: {
      type: String,
      default: ""
    },
    minPrice: {
      type: String,
      default: ""
    },
    /**
     * Product netto weight
     */
    nettoWeight: {
      type: String,
      default: ""
    },
    /**
     * This will force the ADR
     */
    originalPrice: {
      type: String,
      default: ""
    },
    originalPriceLabel: {
      type: String,
      default: ""
    },
    originalPriceType: {
      type: String,
      default: ""
    },
    points: {
      type: String,
      default: null
    },
    price: {
      type: Number,
      default: null
    },
    priceLabel: {
      type: String,
      default: ""
    },
    priceType: {
      type: String,
      default: ""
    },
    product: {
      type: Object,
      default() {
        return {};
      }
    },
    /**
     * Quantity of product available
     */
    quantity: {
      type: Number,
      default: null
    },
    /**
     * Product Sales Event Text
     */
    salesEventText: {
      type: String,
      default: ""
    },
    salesLabel: {
      type: String,
      default: ""
    },
    /**
     * Force showing the ADR price
     */
    showAdrPrice: Boolean,
    showAdrOnly: Boolean,
    showExclusiveOfferMessage: Boolean,
    showOriginalCrossedOut: Boolean,
    showPriceLabels: Boolean,
    showVariantSelector: Boolean,
    /**
     * Product size weight
     */
    sizeWeight: {
      type: String,
      default: ""
    },
    /**
     * Product sku
     */
    sku: {
      type: String,
      default: ""
    },
    /**
     * Warning message, if product is out of stock or whatever
     */
    statusMessage: {
      type: String,
      default: ""
    },
    /**
     * Warning message key, for qa testing automation
     */
    statusMessageKey: {
      type: String,
      default: ""
    },
    title: {
      type: String,
      default: ""
    },
    translations: {
      type: Object,
      default() {
        return {};
      }
    },
    userIsQualified: Boolean,
    userLoyalty: {
      type: Object,
      default() {
        return {};
      }
    },
    /**
     * Array of variant skus for the variant selector, if base sku
     */
    variantSkus: {
      type: Array,
      default() {
        return [];
      }
    },
    /**
     * Array of variant skus for the variant selector, if base sku
     */
    variants: {
      type: Array,
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      columnOne: [],
      columnTwo: [],
      showFavoritesText: false,
      /**
       * Index of the currently selected slide from the image carousel
       */
      slideIndex: 0,
      /**
       * True during slide transitions
       */
      slideTransitioning: false,
      analyticsInterval: null
    };
  },
  computed: {
    desktopAndUp() {
      return ["desktop", "desktopHD"].includes(this.$mq);
    },
    phabletAndDown() {
      return ["mobile", "phablet"].includes(this.$mq);
    },
    showPrices() {
      return (
        (this.price && this.price !== "0") ||
        (this.minPrice && this.minPrice !== "0")
        // Note: We are showing the [retail] price even if the user isn't qualified to purchase
        // !(this.isExclusive && !this.userIsQualified)
      );
    },
    showSkuPoints() {
      return !this.isBaseSku && !(this.hideSku && !this.isDistributor);
    },
    showPayLater() {
      return !this.showAdrOnly && this.enablePayPalText;
    },
    payLaterAmount() {
      return this.price * this.quantity;
    }
  },
  watch: {
    product: {
      immediate: true,
      handler(newValue) {
        this.setDetailInfo(newValue);
      }
    }
  },
  mounted() {},
  destroyed() {
    if (this.analyticsInterval) {
      clearInterval(this.analyticsInterval);
    }
  },
  methods: {
    /**
     * Adds the product to cart
     */
    handleAddToCart() {
      /**
       * Fired when the add to cart button is clicked: function() => void
       *
       * @type {click}
       */
      this.$emit("add-to-cart");
    },
    /**
     * Adds the product to ADR
     */
    handleAddToAdr() {
      /**
       * Fired when the add to Adr button is clicked: function() => void
       *
       * @type {click}
       */
      this.$emit("add-to-adr");
    },
    /**
     * Adds the product to Favorites
     */
    handleFavoriteClick() {
      /**
       * Fired when the add to Favorite icon is clicked: function() => void
       *
       * @type {click}
       */
      this.$emit("favorite-click");
    },
    /**
     * Sets quantity
     */
    handleQuantitySelect(selected, index) {
      /**
       * Fired when a new quantity is selected: function(value: string|number, index: number) => void
       *
       * @type {select}
       * @property {(string|number)} value data-value attribute of the selected item, or index
       * @property {number} index index of the selected item
       */
      this.$emit("quantity-select", selected, index);
    },
    handleShowFavoritesText(shouldShow) {
      this.showFavoritesText = shouldShow;
    },
    /**
     * Sets product to new selection
     */
    handleSelectVariant(selected, index) {
      /**
       * Fired when a new variant is selected: function(value: string|number, index: number) => void
       *
       * @type {select}
       * @property {(string|number)} value data-value attribute of the selected item, or index
       * @property {number} index index of the selected item
       */
      this.$emit("variant-select", selected, index);
    },
    /**
     * Sets product image to new slide selection
     */
    handleSlideSelect(slideIndex) {
      if (slideIndex !== this.slideIndex) {
        this.slideTransitioning = true;

        setTimeout(() => {
          this.slideIndex = slideIndex;
          this.$emit("image-select", slideIndex);
          this.slideTransitioning = false;
        }, 200);
      }
    },
    /**
     * Set content for content blocks on bottom half of pdp
     * @param product
     */
    setDetailInfo(product) {
      if (!product) return;

      this.columnOne = [];
      this.columnTwo = [];

      const BLOCKS = [
        "videos",
        "benefits",
        "usage",
        "ingredients",
        "resources",
        "movie"
      ];
      const detailInfoData = [];

      BLOCKS.forEach(block => {
        if (
          product[block] &&
          (product[block][block + "Text"] ||
            product[block][block + "HTML"] ||
            product[block][block + "Items"])
        ) {
          detailInfoData.push({
            id: block,
            title:
              product[block][block + "Text"] ||
              product[block]["tdc_videosText"] ||
              "",
            content: product[block][block + "HTML"] || "",
            items: product[block][block + "Items"] || [],
            type: block !== "movie" ? block : "videos"
          });
        }
      });

      if (product.youtube && product.youtube.videoID) {
        detailInfoData.push({
          id: "youtube",
          title: product.youtube.tdc_youtubeText || "",
          content:
            "<iframe frameborder='0' scrolling='no' allowfullscreen='allowfullscreen' marginheight='0' marginwidth='0' type='text/html' " +
              "src='https://www.youtube.com/embed/" +
              product.youtube.videoID +
              "?origin=https://nuskin.com' " +
              "height='" +
              product.youtube.height +
              "' width='" +
              product.youtube.width +
              "'></iframe>" || "",
          items: [],
          type: "videos"
        });
      }

      this.setDetailInfoColumns(detailInfoData);

      //If content was not entered in specific drawers check legacy drawers
      if (this.columnOne.length === 0 && this.columnTwo.length === 0) {
        if (
          Array.isArray(product.contentSection) &&
          product.contentSection.length > 0
        ) {
          const legacyDrawerContent = product.contentSection[0];
          if (legacyDrawerContent.sectionContent) {
            this.setDetailInfoColumns(
              this.createLegacyContentDrawerData(
                legacyDrawerContent.sectionContent
              )
            );
          }
        }
      }
    },

    /**
     * Adds content in array to each column depending the size of the content
     */
    setDetailInfoColumns(contentArray) {
      if (Array.isArray(contentArray) && contentArray.length > 0) {
        this.columnOne = contentArray.filter((info, index) => index % 2 === 0);
        this.columnTwo = contentArray.filter((info, index) => index % 2 !== 0);
      }
    },

    /**
     * Programmatically create stacked content blocks
     * @param {string} contents
     */
    createLegacyContentDrawerData(contents) {
      let ele = document.createElement("column-content");
      ele.innerHTML = contents;
      let contentSections = ele.children;
      let data = [];
      if (contentSections && contentSections.length > 0) {
        for (let i = 0; i < contentSections[0].children.length; i++) {
          let el = contentSections[0].children[i];
          let h3 = el.getElementsByTagName("h3");
          let content = el.getElementsByClassName("sectionContent");
          if (h3.length > 0 && content.length > 0 && content[0].innerHTML) {
            data.push({
              id: `legacy-${i}`,
              title: h3[0].innerText,
              content: content[0].innerHTML,
              type: h3
            });
          }
        }
      }

      return data;
    }
  }
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use '~@nuskin/ns-core-styles/src/scss/global.mixins';
@use '~@nuskin/ns-core-styles/src/scss/colors';
@use '~@nuskin/ns-core-styles/src/scss/other';
@use '~@nuskin/ns-core-styles/src/scss/typographyVariables';

* {
  box-sizing: border-box;
}

.ns-product-detail {
  position: relative;
  display: grid;
  grid-gap: 40px;
  padding-left: 15px;
  padding-right: 15px;
  width: 100%;
  color: map.get(colors.$themeColors, color-interactive-7);
  background-color: #ffffff;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}

.ns-product-detail__main-content {
  display: grid;
  grid-gap: 10px;
}

//
// SALES LABEL
//

.ns-product-detail__sales-label {
  /*@apply --subtitle-small-bold-caps;*/
  letter-spacing: 1px;
  position: absolute;
  top: 0;
  left: 0;
  background-color: map.get(colors.$themeColors, color-interactive-7);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  padding: 2px 5px 0;
  height: 20px;
  white-space: nowrap;
}

.ns-product-detail__sales-label::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 100%;
  border-top: 10px solid map.get(colors.$themeColors, color-interactive-7);
  border-bottom: 10px solid map.get(colors.$themeColors, color-interactive-7);
  border-right: 6px solid transparent;
  font-size: 0;
  line-height: 0;
  width: 0;
}

//
// FAVORITE ICON
//

.ns-product-detail__favorite {
  position: absolute;
  bottom: 100%;
  right: 15px;
  height: 20px;
  top: -45px;
}

.ns-product-detail__favorite-text {
  text-align: right;
}

.ns-product-detail__favorite-icon {
  transition: map.get(other.$otherUtils, global-transition);

  &:hover {
    color: map.get(colors.$baseColors, mermaid);
  }

  &.is-favorite {
    color: map.get(colors.$baseColors, mermaid);
  }
}

//
// IMAGES / CAROUSEL
//

.ns-product-detail__images {
  position: relative;
}

.ns-product-detail__active-video {
  width: 100%;
}

.ns-product-detail__active-image {
  display: block;
  position: relative;
  transition: opacity 0.1s ease-in-out;

  &.transitioning {
    opacity: 0;
  }
}

.ns-product-detail-carousel__slide-number {
  text-align: center;
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 1px;
}

.ns-product-detail-carousel__slide-number::first-letter {
  color: map.get(colors.$themeColors, color-interactive-7);
}

//
// RIGHT
//

.ns-product-detail__body--right {
  text-align: center;
}

//
// TITLE
//

.ns-product-detail__title {
  margin-bottom: 10px;
  color: map.get(colors.$themeColors, color-interactive-7);
  font-weight: map.get(typographyVariables.$typography, font-weight-regular);
  @include global.title();
  line-height: normal;

  ::v-deep sup {
    line-height: 1;
    font-size: inherit;
    vertical-align: top;
  }
}

//
// SIZE WEIGHT
//

.ns-product-detail__size-weight {
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
  color: map.get(colors.$themeColors, color-interactive-8);
  text-transform: uppercase;
  line-height: 18px;
}

.ns-product-detail__weight {
  margin-left: 10px;
}

//
// PRICES
//

.ns-product-detail__price {
  justify-content: center;
  margin-bottom: 8px;
  color: map.get(colors.$themeColors, color-interactive-7);
  font-size: 18px;
  line-height: 18px;
}

//
// SKU-POINTS
//

.ns-product-detail__sku-points {
  justify-content: center;
  color: map.get(colors.$themeColors, color-interactive-8);
  font-size: 14px;
  font-weight: map.get(typographyVariables.$typography, font-weight-medium);
  line-height: 18px;
}

//
// LOYALTY-INFORMATION
//

.ns-product-detail__loyalty-information {
  justify-content: center;
  color: map.get(colors.$themeColors, color-interactive-8);
  font-size: 14px;
  font-weight: map.get(typographyVariables.$typography, font-weight-medium);
  line-height: 18px;
}

.ns-loyalty-information {
  margin-top: 10px;
}

//
// EXCLUSIVE
//

.ns-product-detail__exclusive {
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  margin-top: 25px;
}

.exclusive_title {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  color: map.get(colors.$themeColors, color-primary-text);
  @include global.p-large();

  span {
    max-width: 150px;
  }
}

.ns-product-detail__exclusive-icon {
  flex-shrink: 0;
  margin-right: 7px;
  height: 30px;
  width: 30px;
}

.ns-product-detail__exclusive-text {
  font-weight: 600;
  white-space: nowrap;
}

//
// VARIANT AND QUANTITY SELECTORS
//

.ns-product-detail__selectors {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 15px;
  margin-top: 20px;

  .ns-product-detail__quantity-selector:not(.no-variant) ::v-deep .error-tag {
    left: unset;
    right: 0;
  }
}
.ns-product-detail__variant-selector {
  align-self: flex-end;
  width: 100%;
}

//
// ACTION BUTTONS
//

.ns-product-detail__actions {
  display: grid;
  grid-gap: 15px;
  margin-top: 25px;
}

//
// STATUS MESSAGE, DESCRIPTION, SALES EVENT TEXT
//

.ns-product-detail__message {
  font-weight: map.get(typographyVariables.$typography, font-weight-regular);
  color: map.get(colors.$baseColors, color-error);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  margin-top: 13px;
}

.ns-product-detail__pay-later {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  margin-top: 13px;
}

.ns-product-detail__description,
.ns-product-detail__sales-event-text {
  /*@apply --p-medium;*/
  text-align: left;
  max-width: 570px;
  margin-top: 15px;
  font-weight: 300;

  > div {
    display: grid;
    grid-gap: 10px;
  }

  img {
    max-width: 100%;
  }

  > img {
    max-width: 100%;
  }
  ::v-deep ul {
    padding-left: 20px;
  }
  ::v-deep ol {
    padding-left: 20px;
  }
}

//
// NETTO WEIGHT
//

.ns-product-detail__netto-weight {
  display: flex;
  margin-top: 15px;
  color: map.get(colors.$themeColors, color-interactive-8);
  text-align: left;
}

.ns-product-detail__netto-weight span {
  margin-left: 10px;
}

//
// SHIPPING & REFUNDS
//

.ns-product-detail__shipping-refunds {
  margin: 15px 0 10px;
  color: map.get(colors.$themeColors, color-interactive-8);
  line-height: 20px;

  b {
    display: block;
  }

  a {
    position: relative;
    top: 5px;
    border-bottom: 1px solid map.get(colors.$themeColors, color-interactive-7);
    color: map.get(colors.$themeColors, color-interactive-7);
    font-weight: map.get(typographyVariables.$typography, font-weight-semibold);
    text-decoration: none !important;

    &::before {
      content: "\a";
      white-space: pre;
    }
  }
}

.ns-product-detail__shipping-refunds iron-icon,
.ns-product-detail__shipping-refund-icon {
  height: 20px;
  width: 20px;
  margin: 0 auto;
  color: map.get(colors.$themeColors, color-interactive-7);
  margin-bottom: 10px;
}

//
// DETAIL INFO
//

.ns-product-detail__info {
  display: grid;
  grid-gap: 15px;
  grid-auto-rows: max-content;
  margin-bottom: 15px;
}

.ns-product-detail__info-column {
  display: grid;
  grid-gap: 15px;
  grid-auto-rows: max-content;
}

@media (max-width: 767px) {
  .ns-product-detail-carousel__slide-number {
    position: absolute;
    bottom: 23px;
    left: 0;
    right: 0;
    margin: auto;
  }
}

@media (min-width: 768px) {
  .ns-product-detail__main-content {
    grid-auto-flow: column;
    grid-gap: 15px;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  }

  .ns-product-detail-carousel__slide-number {
    font-size: 14px;
    line-height: 17px;
    margin-top: 15px;
  }

  .ns-product-detail__body--right {
    text-align: left;
  }

  .ns-product-detail__title {
    font-size: 28px;
    line-height: 30px;
    font-weight: map.get(typographyVariables.$typography, font-weight-regular);
  }

  .ns-product-detail__size-weight {
    justify-content: flex-start;
  }

  .ns-product-detail__price {
    justify-content: flex-start;
  }

  .ns-product-detail__sku-points {
    justify-content: flex-start;
  }

  .ns-product-detail__loyalty-information {
    justify-content: flex-start;
  }

  .ns-product-detail__exclusive {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
    left: 0;
    right: 0;
    max-width: 580px;
  }

  .ns-product-detail__variant-selector {
    justify-content: flex-start;
  }

  .ns-product-detail__message {
    justify-content: flex-start;
  }

  .ns-product-detail__shipping-refunds {
    display: flex;
    margin-bottom: 0;

    b {
      display: inline;
    }

    a {
      position: static;
      margin-left: 8px;

      &::before {
        white-space: normal;
      }
    }
  }

  .ns-product-detail__shipping-refunds iron-icon,
  .ns-product-detail__shipping-refund-icon {
    margin: 0;
    margin-right: 5px;
  }

  .ns-product-detail__info {
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .ns-product-detail__favorite {
    height: 45px;
  }

  .ns-product-detail-carousel {
    margin-top: 15px;
  }
}

@media (min-width: 1024px) {
  .ns-product-detail__favorite {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    cursor: pointer;
  }

  .ns-product-detail__favorite-text {
    max-width: 100px;
    margin-right: 10px;
    @include global.a-micro-caps();
  }

  .ns-product-detail__images {
    display: grid;
    grid-column-gap: 15px;
    grid-template-columns: 50px 1fr 50px;
    grid-template-areas: "pdpCarousel pdpImage ." ". pdpSlideNumber .";
    position: relative;
  }

  .ns-product-detail__active-image {
    grid-area: pdpImage;
  }

  .ns-product-detail-carousel {
    grid-area: pdpCarousel;
  }

  .ns-product-detail-carousel__slide-number {
    grid-area: pdpSlideNumber;
  }

  .exclusive_title {
    justify-content: flex-start;
    align-items: flex-start;

    span {
      margin-top: 0;
    }
  }

  .ns-product-detail__selectors {
    grid-template-columns: none;
  }

  .ns-product-detail__variant-selector {
    width: 75%;
  }
  .ns-product-detail__actions {
    grid-gap: 25px;
    margin-top: 20px;
  }
}

@media (min-width: 1440px) {
  .ns-product-detail__actions {
    grid-auto-flow: column;
    align-items: flex-end;
    justify-content: flex-start;
    margin-bottom: 30px;
  }

  .ns-product-detail__variant-selector {
    width: 325px;
  }

  .ns-product-detail__actions button {
    min-width: 240px;
  }
}

.ns-product-detail {
  div:empty,
  span:empty {
    display: none;
  }
}
</style>
